import { TagDescription } from "@reduxjs/toolkit/query";
import api from "src/shared/api";
import {
  EQueryTagTypes,
  ISwoolBaseResponse,
  ISwoolResponse,
} from "src/shared/types/api";
import { getProvidesTags } from "src/shared/utils/url/query.helpers";
import {
  IBonusStateItem,
  IGetSummaryReportQuery,
  IPostBonusDTO,
  ISummaryReportItem,
  IUpdateBonusStatesResponseItem,
  IExportSummaryReportQuery,
  IFormedActsZipDTO,
} from "../types";

const summaryReportApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSummaryReportsList: build.query<
      ISwoolResponse<ISummaryReportItem[]>,
      Partial<IGetSummaryReportQuery>
    >({
      query: (params) => {
        // Приходится делать так, потому что RTK не передает данные в корректном формате через params
        const normalizedParams = {
          ...params,
          states:
            typeof params.states === "string"
              ? params.states.split(",")
              : params.states,
        };

        const queryParts: string[] = [];

        Object.entries(normalizedParams).forEach(([key, value]) => {
          if (value === undefined || value === null) return;

          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null) {
                queryParts.push(`${key}[]=${encodeURIComponent(v)}`);
              }
            });
          } else {
            queryParts.push(`${key}=${encodeURIComponent(value)}`);
          }
        });

        const queryString = queryParts.join("&");
        const url = `api/reports/partner/bonuses/summary${queryString ? `?${queryString}` : ""}`;

        return {
          url,
          method: "GET",
        };
      },
      providesTags: (response) => {
        const tags: TagDescription<EQueryTagTypes>[] = [];
        if (!response) {
          return tags;
        }
        tags.push(
          ...getProvidesTags<ISummaryReportItem>(
            response.data,
            EQueryTagTypes.SUMMARY_REPORT,
          ),
          { type: EQueryTagTypes.SUMMARY_REPORT, id: "LIST" },
        );

        return tags;
      },
    }),
    updateBonus: build.mutation<
      ISwoolBaseResponse<ISummaryReportItem>,
      IPostBonusDTO
    >({
      query: ({ id, ...body }) => {
        return {
          url: `api/reports/partner/bonuses/summary/bonus/${id}/update`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (response) => [
        { type: EQueryTagTypes.SUMMARY_REPORT, id: response?.data.id },
      ],
    }),
    updateBonusWithAct: build.mutation<
      ISwoolBaseResponse<ISummaryReportItem>,
      IPostBonusDTO
    >({
      query: ({ id, ...body }) => {
        return {
          url: `api/reports/partner/bonuses/summary/bonus/${id}/update-with-act`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (response) => [
        { type: EQueryTagTypes.SUMMARY_REPORT, id: response?.data.id },
      ],
    }),
    updateBonusNote: build.mutation<
      ISwoolBaseResponse<ISummaryReportItem>,
      { id: number; note: string }
    >({
      query: ({ id, ...body }) => {
        return {
          url: `api/reports/partner/bonuses/summary/bonus/${id}/update-note`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (response) => [
        { type: EQueryTagTypes.SUMMARY_REPORT, id: response?.data.id },
      ],
    }),
    postBonusStates: build.mutation<
      ISwoolBaseResponse<Array<IUpdateBonusStatesResponseItem>>,
      Array<IBonusStateItem>
    >({
      query: (body) => {
        return {
          url: `api/reports/partner/bonuses/summary/bonus-states/update`,
          method: "POST",
          body: { bonuses: body },
        };
      },
      invalidatesTags: (response) => {
        if (!response?.data) {
          return [];
        }

        return response?.data.reduce(
          (acc: { type: EQueryTagTypes; id: string }[], item) => {
            if (item.updated) {
              acc.push({
                type: EQueryTagTypes.SUMMARY_REPORT,
                id: String(item.id),
              });
            }
            // if not updated - don't invalidate

            return acc;
          },
          [],
        );
      },
    }),
    formActs: build.mutation<
      { success: boolean; message: string },
      { agency: number }
    >({
      query: (body) => {
        return {
          url: `api/reports/partner/bonuses/summary/reward/form-acts`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: () => [
        { type: EQueryTagTypes.SUMMARY_REPORT, id: "List" },
      ],
    }),
    exportSummaryReport: build.query<
      string,
      Partial<IExportSummaryReportQuery>
    >({
      query: (params) => {
        // Приходится делать так, потому что RTK не передает данные в корректном формате через params
        const normalizedParams = {
          ...params,
          states:
            typeof params.states === "string"
              ? params.states.split(",")
              : params.states,
        };

        const queryParts: string[] = [];

        Object.entries(normalizedParams).forEach(([key, value]) => {
          if (value === undefined || value === null) return;

          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null) {
                queryParts.push(`${key}[]=${encodeURIComponent(v)}`);
              }
            });
          } else {
            queryParts.push(`${key}=${encodeURIComponent(value)}`);
          }
        });

        const queryString = queryParts.join("&");
        const url = `api/reports/partner/bonuses/summary/export${queryString ? `?${queryString}` : ""}`;

        return {
          url,
          method: "GET",
          responseHandler: (response) => response.text(),
        };
      },
      transformResponse: (response: string) => response,
    }),
    getFormedActsZip: build.query<IFormedActsZipDTO, string>({
      query: (reportMonth) => {
        return {
          url: "/api/reports/partner/bonuses/summary/reward/form-acts",
          method: "GET",
          params: { report_month: reportMonth },
        };
      },
    }),
    sendActsToEmail: build.mutation({
      query: ({ data }) => {
        return {
          url: `/api
/reports/partner/bonuses/summary/reward/form-acts/send`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: () => [
        { type: EQueryTagTypes.SUMMARY_REPORT, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetSummaryReportsListQuery,
  useUpdateBonusMutation,
  useUpdateBonusWithActMutation,
  useUpdateBonusNoteMutation,
  usePostBonusStatesMutation,
  useFormActsMutation,
  useLazyExportSummaryReportQuery,
  useLazyGetFormedActsZipQuery,
  useSendActsToEmailMutation,
} = summaryReportApi;
